import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DEFAULT_PAGE_FILTERS, issueStatusKey, type ListItemKind } from '@d-orca/shared/client/activities';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { useAppSelector } from '@store/hooks';
import {
  selectActivityFilter,
  selectDisplayFloorIds,
  selectIssueFilter,
  selectMonitorLayer,
  selectProfessions,
  selectSpecialFloors,
} from '@store/selectors/monitor.selectors';
import { useUnifiedList } from '../../ActivitiesPage/hooks/useUnifiedList';
import type { IListItem } from '../../ActivitiesPage/model/listItem';
import {
  ACTIVITY_FILTERS,
  chipsByFloor,
  countBy,
  ISSUE_FILTERS,
  itemFloors,
  itemMatchesActivityFilter,
  itemMatchesIssueFilter,
  itemTrade,
  type ActivityFilter,
  type IItemTrade,
  type ILayerChip,
  type IssueFilter,
  type MonitorLayer,
} from './layers';

// Both kinds in one pass, every status but a closed issue's: the Complete
// pill needs the done activities the page's default hides; a closed issue is
// history.
const LAYER_FILTERS = {
  ...DEFAULT_PAGE_FILTERS,
  types: ['activity', 'issue'] satisfies ListItemKind[],
  hiddenStatuses: [issueStatusKey('completed')],
};

/** One kind's located items, as its list, chips and drawer tab read them. */
export interface ILocatedKind<F extends string> {
  /** Every located item of this kind — the title's count. */
  located: IListItem[];
  /** The located items under the kind's picked pill — the list, the chips, the drawer. */
  items: IListItem[];
  counts: Record<F, number>;
  /** The kind's picked pill. */
  filter: F;
  loading: boolean;
  error: boolean;
}

export interface ILocatedItems {
  activities: ILocatedKind<ActivityFilter>;
  issues: ILocatedKind<IssueFilter>;
  /** The building floors an item sits on. */
  floorsOf: (item: IListItem) => string[];
  /** An activity's areas → the floor each sits on. */
  floorOfArea: ReadonlyMap<string, string>;
  /** An item's trade pill and contractor. */
  tradeOf: (item: IListItem) => IItemTrade | undefined;
}

function locatedKind<F extends string>(
  located: IListItem[],
  filters: readonly F[],
  matches: (item: IListItem, filter: F) => boolean,
  filter: F,
  loading: boolean,
  error: boolean,
): ILocatedKind<F> {
  return {
    located,
    items: located.filter((item) => matches(item, filter)),
    counts: countBy(filters, located, matches),
    filter,
    loading,
    error,
  };
}

/**
 * The site's activities and issues on the building: the Activities & Issues
 * page's own (the schedule picked in the top bar for activities, the site's
 * issues), kept to those located on a floor the building draws or in a special
 * area, then narrowed by each kind's filter pill. One `useUnifiedList` pass
 * serves both kinds. `siteId` undefined loads nothing.
 */
export function useLocatedItems(siteId: string | undefined): ILocatedItems {
  const { items: all, activitiesLoading, issuesLoading, activitiesError, issuesError } = useUnifiedList(
    siteId,
    LAYER_FILTERS,
  );
  const { floors: siteFloors } = useSiteLocations(siteId ?? '');
  const displayFloorIds = useAppSelector(selectDisplayFloorIds);
  const specialFloors = useAppSelector(selectSpecialFloors);
  const catalog = useAppSelector(selectProfessions);
  const activityFilter = useAppSelector(selectActivityFilter);
  const issueFilter = useAppSelector(selectIssueFilter);

  const floorOfArea = useMemo(
    () => new Map(siteFloors.flatMap((floor) => floor.zones.map((zone) => [zone.id, floor.id] as const))),
    [siteFloors],
  );
  // The building site's floors: those the building drawing draws (its own
  // floor list; without one, the site's location catalog) plus the special
  // areas the Special Areas card lists.
  const buildingFloors = useMemo(
    () =>
      new Set([
        ...(displayFloorIds ?? siteFloors.map((floor) => floor.id)),
        ...specialFloors.map((floor) => floor.floorId).filter((id): id is string => id !== undefined),
      ]),
    [displayFloorIds, siteFloors, specialFloors],
  );
  const floorsOf = useMemo(
    () => (item: IListItem) => itemFloors(item, floorOfArea, buildingFloors),
    [floorOfArea, buildingFloors],
  );
  const tradeOf = useMemo(() => (item: IListItem): IItemTrade | undefined => itemTrade(item, catalog), [catalog]);

  const located = useMemo(() => all.filter((item) => floorsOf(item).length > 0), [all, floorsOf]);
  const activities = useMemo(
    () =>
      locatedKind(
        located.filter((item) => item.kind === 'activity'),
        ACTIVITY_FILTERS,
        itemMatchesActivityFilter,
        activityFilter,
        activitiesLoading,
        activitiesError,
      ),
    [located, activityFilter, activitiesLoading, activitiesError],
  );
  const issues = useMemo(
    () =>
      locatedKind(
        located.filter((item) => item.kind === 'issue'),
        ISSUE_FILTERS,
        itemMatchesIssueFilter,
        issueFilter,
        issuesLoading,
        issuesError,
      ),
    [located, issueFilter, issuesLoading, issuesError],
  );
  return useMemo(
    () => ({ activities, issues, floorsOf, floorOfArea, tradeOf }),
    [activities, issues, floorsOf, floorOfArea, tradeOf],
  );
}

/** The Monitor page's layer data, loaded once for the rail, the building, the
 *  special areas and the floor drawer. Absent outside the Monitor page. */
export interface ILayerData extends ILocatedItems {
  layer: MonitorLayer;
  siteId: string;
}

export const LayerDataContext = createContext<ILayerData | null>(null);

/** Loads the page's layer data — nothing in the Workforce layer. */
export function useLayerDataSource(siteId: string): ILayerData {
  const layer = useAppSelector(selectMonitorLayer);
  const located = useLocatedItems(layer === 'workforce' ? undefined : siteId);
  return useMemo(() => ({ ...located, layer, siteId }), [located, layer, siteId]);
}

/** The Monitor page's layer data — the rail list reads it, as the building's
 *  chips do, so the two always show the same items. */
export function useLayerData(): ILayerData {
  const data = useContext(LayerDataContext);
  if (!data) throw new Error('useLayerData must be used under the Monitor page (LayerDataContext)');
  return data;
}

/**
 * A floor's (or special area's) activities and issues for its drawer's tabs —
 * the same items, under the same pills, as the layer's list and chips. The
 * page's data serves them in the Activities and Issues layers; in the
 * Workforce layer (no page data) the drawer loads its own — its tabs count them.
 */
export function useFloorItems(floorId: string) {
  const { siteId } = useParams<{ siteId: string }>();
  const page = useContext(LayerDataContext);
  const pageLoaded = page !== null && page.layer !== 'workforce';
  const own = useLocatedItems(pageLoaded ? undefined : siteId);
  const { activities, issues, floorsOf, tradeOf } = pageLoaded ? page : own;
  const onFloor = (item: IListItem) => floorsOf(item).includes(floorId);
  return {
    activities: activities.items.filter(onFloor),
    issues: issues.items.filter(onFloor),
    tradeOf,
    issuesLoading: issues.loading,
    issuesError: issues.error,
  };
}

export interface ILayerChips {
  layer: 'activities' | 'issues';
  byFloor: Map<string, ILayerChip[]>;
  floorOfArea: ReadonlyMap<string, string>;
}

/** The active layer's chips by floorId, or null in the Workforce layer and
 *  outside the Monitor page (the Dashboard's building). */
export function useLayerChips(): ILayerChips | null {
  const data = useContext(LayerDataContext);
  return useMemo(() => {
    if (!data || data.layer === 'workforce') return null;
    const { items } = data.layer === 'issues' ? data.issues : data.activities;
    return {
      layer: data.layer,
      byFloor: chipsByFloor(items, data.floorsOf, data.tradeOf, data.floorOfArea),
      floorOfArea: data.floorOfArea,
    };
  }, [data]);
}
